// client/src/pages/PTO/PTODataTable.tsx
// Searchable/filterable data table for all PTO entries.

import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Search, Pencil, X, Trash2, History } from 'lucide-react';
import type { TableColumn } from 'react-data-table-component';
import DataTable from '../../components/DataTable';
import type { PTOEntry, PTOEmployee, EntryType, Office } from './ptoTypes';
import { ENTRY_TYPES, OFFICES } from './ptoConstants';
import { formatDate, getEntryTypeStyle, getOfficeColor } from './ptoUtils';

// ─── Props ──────────────────────────────────────────────────────────────────

interface PTODataTableProps {
  entries: PTOEntry[];
  employees: PTOEmployee[];
  onEdit: (entry: PTOEntry) => void;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => Promise<void>;
  onEmployeeClick: (emp: PTOEmployee) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface EnrichedEntry extends PTOEntry {
  employeeName: string;
  office: string;
  employee?: PTOEmployee;
}

// ─── Component ──────────────────────────────────────────────────────────────

const PTODataTable: React.FC<PTODataTableProps> = ({
  entries,
  employees,
  onEdit,
  onDelete,
  onBulkDelete,
  onEmployeeClick,
}) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<EntryType | ''>('');
  const [officeFilter, setOfficeFilter] = useState<Office | ''>('');
  const [monthFilter, setMonthFilter] = useState<number | ''>('');
  const [showPast, setShowPast] = useState(false);
  const [selectedRows, setSelectedRows] = useState<EnrichedEntry[]>([]);
  const [clearToggle, setClearToggle] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Build employee lookup
  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Enrich entries with employee data, filter out unknown employees
  const enriched = useMemo<EnrichedEntry[]>(() => {
    return entries
      .filter((entry) => empMap.has(entry.staffIndex))
      .map((entry) => {
        const emp = empMap.get(entry.staffIndex)!;
        return {
          ...entry,
          employeeName: emp.displayName,
          office: emp.office ?? '',
          employee: emp,
        };
      });
  }, [entries, empMap]);

  // Filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const today = todayIso();
    return enriched.filter((row) => {
      // Past-date filter (hidden by default)
      if (!showPast && row.entryDate < today) return false;
      // Search filter
      if (q) {
        const haystack = `${row.employeeName} ${row.note ?? ''} ${row.entryType}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // Type filter
      if (typeFilter && row.entryType !== typeFilter) return false;
      // Office filter
      if (officeFilter && row.office !== officeFilter) return false;
      // Month filter
      if (monthFilter !== '') {
        const entryMonth = Number(row.entryDate.slice(5, 7));
        if (entryMonth !== monthFilter) return false;
      }
      return true;
    });
  }, [enriched, search, typeFilter, officeFilter, monthFilter, showPast]);

  const pastCount = useMemo(() => {
    const today = todayIso();
    return enriched.filter((r) => r.entryDate < today).length;
  }, [enriched]);

  const handleBulkDeleteClick = useCallback(async () => {
    if (selectedRows.length === 0) return;
    const ok = window.confirm(
      `Delete ${selectedRows.length} ${selectedRows.length === 1 ? 'entry' : 'entries'}? This cannot be undone.`,
    );
    if (!ok) return;
    setBulkDeleting(true);
    try {
      await onBulkDelete(selectedRows.map((r) => r.id));
      setSelectedRows([]);
      setClearToggle((t) => !t);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedRows, onBulkDelete]);

  // Columns
  const columns = useMemo<TableColumn<EnrichedEntry>[]>(
    () => [
      {
        name: 'Date',
        selector: (row) => row.entryDate,
        cell: (row) => (
          <span data-tag="allowRowEvents" style={{ fontSize: 13 }}>
            {formatDate(row.entryDate)}
          </span>
        ),
        sortable: true,
        width: '110px',
      },
      {
        name: 'Employee',
        selector: (row) => row.employeeName,
        cell: (row) => (
          <EmpButton
            onClick={(e) => {
              e.stopPropagation();
              if (row.employee) onEmployeeClick(row.employee);
            }}
          >
            {row.employeeName}
          </EmpButton>
        ),
        sortable: true,
        grow: 2,
      },
      {
        name: 'Office',
        selector: (row) => row.office,
        cell: (row) => {
          if (!row.office) return <span className="muted">--</span>;
          const color = getOfficeColor(row.office);
          return (
            <OfficePill $color={color}>{row.office}</OfficePill>
          );
        },
        sortable: true,
        width: '130px',
      },
      {
        name: 'Type',
        selector: (row) => row.entryType,
        cell: (row) => {
          const style = getEntryTypeStyle(row.entryType);
          return (
            <TypeBadge $bg={style.bg} $color={style.color}>
              {row.entryType}
            </TypeBadge>
          );
        },
        sortable: true,
        width: '140px',
      },
      {
        name: 'Hours',
        selector: (row) => row.hours ?? 0,
        cell: (row) => (
          <span data-tag="allowRowEvents" style={{ fontSize: 13 }}>
            {row.hours ? `${row.hours}h` : '\u2014'}
          </span>
        ),
        sortable: true,
        width: '80px',
        right: true,
      },
      {
        name: 'Details',
        selector: (row) => row.note ?? '',
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
            {row.note ? (
              <NoteText title={row.note}>{row.note}</NoteText>
            ) : null}
            <SourceTag $source={row.source}>
              {row.source === 'pe' ? 'PE' : row.source === 'seed' ? 'Imported' : 'Manual'}
            </SourceTag>
          </div>
        ),
        grow: 2,
      },
      {
        name: '',
        cell: (row) => {
          if (row.source === 'pe') return null;
          return (
            <ActionRow>
              <ActionBtn
                title="Edit entry"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
              >
                <Pencil size={13} />
              </ActionBtn>
              <ActionBtn
                title="Delete entry"
                $danger
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row.id);
                }}
              >
                <X size={14} />
              </ActionBtn>
            </ActionRow>
          );
        },
        width: '80px',
        right: true,
      },
    ],
    [onEdit, onDelete, onEmployeeClick],
  );

  return (
    <Wrap>
      {/* Filter bar */}
      <FilterBar>
        <SearchWrap>
          <SearchIcon>
            <Search size={14} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search name, notes, type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>

        <FilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as EntryType | '')}
        >
          <option value="">All Types</option>
          {ENTRY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={officeFilter}
          onChange={(e) => setOfficeFilter(e.target.value as Office | '')}
        >
          <option value="">All Offices</option>
          {OFFICES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={monthFilter}
          onChange={(e) =>
            setMonthFilter(e.target.value === '' ? '' : Number(e.target.value))
          }
        >
          <option value="">All Months</option>
          {MONTHS.map((label, i) => (
            <option key={i} value={i + 1}>
              {label}
            </option>
          ))}
        </FilterSelect>

        <PastToggle
          type="button"
          $active={showPast}
          onClick={() => setShowPast((v) => !v)}
          title={showPast ? 'Hide entries before today' : 'Show entries before today'}
        >
          <History size={13} />
          {showPast
            ? 'Hide past'
            : pastCount > 0
              ? `Show past (${pastCount})`
              : 'Show past'}
        </PastToggle>
      </FilterBar>

      {/* Bulk-action bar (only when something is selected) */}
      {selectedRows.length > 0 && (
        <BulkBar>
          <BulkCount>
            {selectedRows.length} selected
          </BulkCount>
          <BulkDeleteBtn
            type="button"
            onClick={handleBulkDeleteClick}
            disabled={bulkDeleting}
          >
            <Trash2 size={13} />
            {bulkDeleting
              ? 'Deleting...'
              : `Delete ${selectedRows.length} ${selectedRows.length === 1 ? 'entry' : 'entries'}`}
          </BulkDeleteBtn>
        </BulkBar>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={filtered}
        defaultSortFieldId={1}
        defaultSortAsc
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25, 50, 100]}
        selectableRows
        selectableRowDisabled={(row) => row.source === 'pe'}
        onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
        clearSelectedRows={clearToggle}
        noDataComponent={
          <EmptyMsg>No entries match the current filters.</EmptyMsg>
        }
      />
    </Wrap>
  );
};

export default PTODataTable;

// ─── Styled Components ──────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.12);
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1 1 200px;
  min-width: 180px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 10px 7px 32px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder {
    color: var(--muted);
    opacity: 0.6;
  }

  &:focus {
    border-color: var(--accent);
  }
`;

const FilterSelect = styled.select`
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    border-color: var(--accent);
  }

  option {
    background: #1a1d24;
    color: var(--text);
  }
`;

const PastToggle = styled.button<{ $active: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 11px;
  border-radius: 6px;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(122,162,255,.45)' : 'var(--border)')};
  background: ${({ $active }) =>
    $active ? 'rgba(122,162,255,.12)' : 'rgba(255,255,255,.04)'};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--muted)')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $active }) =>
      $active ? 'rgba(122,162,255,.2)' : 'rgba(255,255,255,.08)'};
    color: var(--text);
  }
`;

const EmpButton = styled.button`
  all: unset;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: var(--accent);
  }
`;

const OfficePill = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $color }) => $color}33;
  color: ${({ $color }) => $color};
`;

const TypeBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const NoteText = styled.span`
  font-size: 12px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--muted);
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? 'rgba(255,107,107,.15)' : 'rgba(255,255,255,.08)'};
    color: ${({ $danger }) => ($danger ? '#ff6b6b' : 'var(--text)')};
  }
`;

const SourceTag = styled.span<{ $source: string }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $source }) =>
    $source === 'pe'
      ? 'rgba(87,211,140,.12)'
      : $source === 'seed'
        ? 'rgba(167,179,196,.1)'
        : 'rgba(122,162,255,.12)'};
  color: ${({ $source }) =>
    $source === 'pe'
      ? 'var(--good)'
      : $source === 'seed'
        ? 'var(--muted)'
        : 'var(--accent)'};
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  background: rgba(122, 162, 255, 0.08);
  border-bottom: 1px solid rgba(122, 162, 255, 0.25);
`;

const BulkCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
`;

const BulkDeleteBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid rgba(255, 107, 107, 0.4);
  background: rgba(255, 107, 107, 0.1);
  color: #ff8b8b;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: rgba(255, 107, 107, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: var(--muted);
  font-size: 13px;
`;
